import React, { useEffect, useState, useRef } from 'react';
import './CustomCircularProgress.css';

const CustomCircularProgressTwoValues = ({
  size,
  progressColors,
  targetValues,
  rotationAngle,
  lineBreakValue,
  textValue1,
  textValue2
}) => {
  const radius = (size - 4) / 2;
  const strokeWidth = 20;
  const trackColor = '#ccc';

  const totalValue = targetValues.reduce((sum, value) => sum + value, 0);

  // State to track whether animation has been played
  const [animationPlayed, setAnimationPlayed] = useState(false);

  // Reference to the chart container element
  const chartRef = useRef(null);

  useEffect(() => {
    // Intersection Observer setup
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animationPlayed) {
        setAnimationPlayed(true);

        const observers = [];

        targetValues.forEach((targetValue, index) => {
          let startTime = null;
          const duration = 1750;

          const animate = (timestamp) => {
            if (!startTime) {
              startTime = timestamp;
            }

            const elapsedTime = timestamp - startTime;
            const progress = Math.min(elapsedTime / duration, 1);
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            const animatedValue = easedProgress * targetValue;

            setValues((prevValues) => {
              const newValues = [...prevValues];
              newValues[index] = animatedValue;
              return newValues;
            });

            if (elapsedTime < duration) {
              requestAnimationFrame(animate);
            }
          };

          requestAnimationFrame(animate);
          observers.push(animate);

          return () => {
            observers.forEach((observer) => {
              observer();
            });
            setTimeout(() => {
              setValues(targetValues);
            }, duration);
          };
        });
      }
    }, observerOptions);

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
    };
  }, [targetValues, animationPlayed]);

  const centerX = size / 2;
  const centerY = size / 2;

  const [values, setValues] = useState(targetValues.map(() => 0));

  const segments = [];
  let startAngle = -90 + rotationAngle;

  // Generate SVG path elements for each segment
  for (let i = 0; i < targetValues.length; i++) {
    const angle = (360 * values[i]) / totalValue;
    const endAngle = startAngle + angle;

    const startRadians = (startAngle * Math.PI) / 180;
    const endRadians = (endAngle * Math.PI) / 180;

    const startX = centerX + radius * Math.cos(startRadians);
    const startY = centerY + radius * Math.sin(startRadians);
    const endX = centerX + radius * Math.cos(endRadians);
    const endY = centerY + radius * Math.sin(endRadians);

    const largeArcFlag = angle <= 180 ? 0 : 1;

    const pathData = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;

    const color = progressColors[i] || '#4CAF50';
    segments.push(<path key={i} d={pathData} fill="none" stroke={color} strokeWidth={strokeWidth} />);
    startAngle = endAngle;
  }

  return (
    <div ref={chartRef} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ width: size, height: size, overflow: 'visible' }}>
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke={trackColor} strokeWidth={strokeWidth} />
        <circle cx={centerX} cy={centerY} r={radius - strokeWidth / 2} fill="white" />
        {segments}
      </svg>
      <div style={{ textAlign: 'center', position: 'absolute', marginLeft: '-20px', marginTop: '10px' }}>
        {/* Text for value 1 */}
        <p
          style={{
            display: 'inline-block',
            margin: '0.5em',
            fontSize: '4rem',
            color: '#000',
            fontWeight: '600',
          }}
        >
          <span style={{ position: 'relative', display: 'block', marginBottom: '5px', marginLeft: '10px' }}>
            <span
              style={{
                position: 'absolute',
                left: '0px',
                top: '20px',
                transform: 'translateX(-50%)',
              }}
            ></span>
            {`${values[1].toFixed(0)}`}%
          </span>
          <span style={{ fontSize: '1rem', color: '#4D4D4D', fontWeight: '500', display: 'block', marginTop: '-20px' }}>
            {textValue1}
          </span>
        </p>
        {/* Text for value 2 */}
        <p
          className='iphoneTargeting'
          style={{
            position: 'absolute',
            display: 'inline-block',
            margin: '0.5em',
            marginLeft: '-15px',
            fontSize: '4rem',
            color: '#8C8C8C',
            fontWeight: '600',
          }}
        >
          <span style={{ position: 'relative', display: 'block', marginBottom: '5px' }}>
            <span
              style={{
                position: 'absolute',
                left: '-11.5px',
                top: '20px',
                transform: 'translateX(-50%)'
              }}
            ></span>
            {`${values[0].toFixed(0)}`}%
          </span>
          <span style={{ fontSize: '1rem', color: '#4D4D4D', fontWeight: '500', display: 'block', marginTop: '-20px', marginLeft: '-10px' }}>
            {textValue2}
          </span>
        </p>
      </div>
    </div>
  );
};

export default CustomCircularProgressTwoValues;
